<template>
	<div class="ks-videoeditor-source-body">
		<div class="ks-videoeditor-inline" style="margin-bottom: 8px;">
			<k-btn size="s" :color="content_mode == 'video' ? 'primary' : ''" @click="content_mode = 'video'">Videos</k-btn>
			<k-btn size="s" :color="content_mode == 'image' ? 'primary' : ''" @click="content_mode = 'image'">Images</k-btn>
			<k-btn size="s" :color="content_mode == 'audio' ? 'primary' : ''" @click="content_mode = 'audio'">Audio</k-btn>
		</div>
		<k-autocomplete
			:items="content_lists"
			v-model="content_list_id"
			:label="{video: 'Clips list', image: 'Images list', audio: 'Audio list'}[content_mode]"
			item-text="name"
			item-value="id"
			clearable>
		</k-autocomplete>
		<div class="ks-videoeditor-list">
			<div v-for="item in content_clip_items" :key="item.id" class="ks-videoeditor-row" @dblclick="add_content_item(item)">
				<img v-if="content_mode == 'image' && item.url" class="ks-videoeditor-thumb" :src="item.url" draggable="false" alt="">
				<div class="ks-videoeditor-row-main">
					<div class="ks-videoeditor-row-title">{{ item.title }}</div>
					<div class="ks-videoeditor-row-sub">{{ item.url ? "" : "No file" }}</div>
				</div>
				<template v-if="content_mode == 'image'">
					<k-btn size="s" icon icon-name="movie" title="Add to video track" :disabled="!item.url" @click="add_content_image(item)"></k-btn>
					<k-btn size="s" icon icon-name="layers" title="Add as overlay" :disabled="!item.url" @click="add_overlay(item)"></k-btn>
				</template>
				<k-btn v-else size="s" icon icon-name="add" :loading="adding == item.id" :disabled="!item.url" @click="add_content_item(item)"></k-btn>
			</div>
			<div v-if="content_list_id && content_clip_items.length == 0" class="ks-videoeditor-empty">No items</div>
		</div>
	</div>
</template>

<script>
import {DEFAULT_IMAGE_SECONDS} from './constants.js'

// Videos, images and audio files from the content lists
export default {
	data () {
		return {
			content_list_id: undefined,
			content_mode: "video",
			content_channel: undefined,
			content_data: {},
			adding: undefined
		}
	},
	beforeDestroy () {
		if (this.content_channel) this.content_channel.leave();
	},
	watch: {
		content_list_id () {
			this.connect_content();
		},
		content_mode () {
			this.content_list_id = undefined;
		}
	},
	computed: {
		list_types () {
			return this.$store.state.cms_list_types;
		},
		content_lists () {
			return _.filter(Object.values(this.$store.state.cms_lists), (list) => {
				const lt = this.list_types[list.cms_list_type_id];
				return !!this.find_media_field(lt, this.content_mode);
			});
		},
		content_list_type () {
			const list = _.find(this.content_lists, {id: this.content_list_id});
			return list ? this.list_types[list.cms_list_type_id] : undefined;
		},
		content_video_field () {
			return this.find_media_field(this.content_list_type, this.content_mode);
		},
		content_clip_items () {
			const field = this.content_video_field;
			const fields = _.get(this.content_list_type, "fields.fields", []);
			const text_field = _.find(fields, (f) => f.type == "text");
			const items = _.map(Object.values(this.content_data), (item) => {
				const value = field ? _.get(item, ["data", field.name]) : undefined;
				const url = _.isObject(value) ? value.url : value;
				const title = (text_field && _.get(item, ["data", text_field.name])) || item.identifier || ("#" + item.id);
				return {id: item.id, item: item, url: url, title: title, state: item.state};
			});
			return _.sortBy(_.filter(items, (i) => i.state != "archived"), (i) => String(i.title).toLowerCase());
		}
	},
	methods: {
		connect_content () {
			if (this.content_channel) this.content_channel.leave();
			this.content_channel = undefined;
			this.content_data = {};
			if (!this.content_list_id) return;
			const channel = this.$cloudserver.channel("cms:list_datas:" + this.content_list_id);
			channel.on("init", (msg) => {
				_.each(msg.list_data, (d) => { this.$set(this.content_data, d.id, d); });
			});
			channel.on("update", (msg) => { this.$set(this.content_data, msg.list_data.id, msg.list_data); });
			channel.on("delete", (msg) => { this.$delete(this.content_data, msg.list_data.id); });
			channel.join();
			this.content_channel = channel;
		},
		probe_duration (url, tag) {
			return new Promise((resolve, reject) => {
				const v = document.createElement(tag || "video");
				v.preload = "metadata";
				v.onloadedmetadata = () => { resolve(v.duration); v.removeAttribute("src"); v.load(); };
				v.onerror = () => reject();
				v.src = url;
			});
		},
		find_media_field (lt, kind) {
			const fields = _.get(lt, "fields.fields", []);
			const file_fields = _.filter(fields, (f) => f.type == "s3file" || f.type == "file");
			const typed = _.find(file_fields, (f) => f["content-type"] == kind);
			// Video and audio accept any file field; images require content-type image
			return typed || (kind == "image" ? undefined : file_fields[0]);
		},
		content_source (item) {
			return {type: "content", list_id: this.content_list_id, item_id: item.id, field: _.get(this.content_video_field, "name")};
		},
		add_content_item (item) {
			if (this.content_mode == "image") this.add_content_image(item);
			else if (this.content_mode == "audio") this.add_audio(item);
			else this.add_content_clip(item);
		},
		add_content_image (item) {
			if (!item.url) return;
			this.$emit("add-clip", {
				type: "image",
				title: String(item.title),
				url: item.url,
				poster_url: item.url,
				duration: DEFAULT_IMAGE_SECONDS,
				source: this.content_source(item)
			});
		},
		add_overlay (item) {
			if (!item.url) return;
			this.$emit("add-overlay", {title: String(item.title), url: item.url, source: this.content_source(item)});
		},
		add_audio (item) {
			if (!item.url) return;
			this.adding = item.id;
			this.probe_duration(item.url, "audio")
				.then((duration) => {
					if (!isFinite(duration) || duration <= 0) throw new Error("no duration");
					this.$emit("add-audio", {title: String(item.title), url: item.url, source: this.content_source(item), duration: duration});
				})
				.catch(() => { this.$message.warning("Unable to read audio: " + item.title); })
				.then(() => { this.adding = undefined; });
		},
		add_content_clip (item) {
			if (!item.url) return;
			this.adding = item.id;
			this.probe_duration(item.url)
				.then((duration) => {
					this.$emit("add-clip", {
						title: String(item.title),
						url: item.url,
						duration: duration,
						source: this.content_source(item)
					});
				})
				.catch(() => { this.$message.warning("Unable to read video: " + item.title); })
				.then(() => { this.adding = undefined; });
		}
	}
}
</script>

<style scoped>
.ks-videoeditor-source-body { padding-top: 8px; }
.ks-videoeditor-list { margin-top: 8px; }
.ks-videoeditor-thumb { height: 32px; width: auto; max-width: 56px; object-fit: contain; flex: none; background: #000; border-radius: 2px; }
</style>
